import { useUserAuthentication } from '@ohif/ui';
import {
  CitizenIdRequiredPage,
  ErrorPage,
  LoadingIndicatorProgress,
  PasswordRequiredPage,
  PasswordEmailRequiredPage,
  SeriesNotFound,
} from '@vindr/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { pushUser } from '../services/analytic.service';
import {
  fetchHashPermissions,
  fetchPermissionToken,
  refreshToken,
  verifyEncrypted,
  verifyHash,
} from '../services/auth.service';
import { getRelatedParams } from '../utils/helpers';
import { ServicesManager } from '@ohif/core';
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
} from '@vindr/utils/auth';
import { useTranslation } from 'react-i18next';
import { isConsultationMode } from '@vindr/utils/helpers';
import { MESSAGE } from '@vindr/services/constants/message.constant';

const FULL_PERMISSIONS = [
  'view_studylist',
  'viewer.download',
  'viewer.keyimage',
  'viewer.deletestudy',
  'viewer.recordannotation',
  'view_report',
];

enum ERROR_TYPES {
  EXPIRED = 'Link Expired',
  NOT_FOUND = 'Not Found',
  NO_PERMISSION = 'Forbidden',
  UNKNOWN = 'Something went wrong',
}

export const PrivateRoute = ({
  children,
  handleUnauthenticated,
  userAuthenticationService,
  httpClientService,
}: {
  children: React.ReactNode;
  userAuthenticationService: ServicesManager['services']['userAuthenticationService'];
  httpClientService: ServicesManager['services']['httpClientService'];
  handleUnauthenticated: ServicesManager['services']['userAuthenticationService']['handleUnauthenticated'];
}) => {
  const [{ user, enabled }] = useUserAuthentication();
  const { t } = useTranslation('Vindoc');

  const queryParams = new URLSearchParams(location.search);
  const accessKey = queryParams.get('access_key');
  const citizenIdRequired = queryParams.get('cccd') === 'true';
  const isEncryptedAuth = queryParams.get('auth_type') === 'encrypted';
  const isConsultationGuest = queryParams.get('consultation_guest') === 'true';
  const [isLoading, setIsLoading] = useState(!!accessKey);
  const [error, setError] = useState({
    isError: false,
    type: null,
  });
  const [invalidPasscode, setInvalidPasscode] = useState<{
    invalid: boolean;
    message?: string;
  }>({
    invalid: false,
    message: '',
  });
  const [invalidCitizenId, setInvalidCitizenId] = useState(false);
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const accessToken = getAccessToken();
  const [permissionTokenReady, setPermissionTokenReady] = useState(false);
  const [isConsultationPasswordRequired, setIsConsultationPasswordRequired] =
    useState(isConsultationMode() && (accessToken || isConsultationGuest));
  const navigate = useNavigate();

  const rfToken = getRefreshToken();
  const [relatedInfo, setRelatedInfo] = useState(getRelatedParams(queryParams));
  const relatedParams = new URLSearchParams(relatedInfo); // params can be update the citizen id

  const checkPermissionForHash = relatedInfo => {
    fetchHashPermissions(relatedInfo)
      .then(res => {
        userAuthenticationService.setUser({
          ...user,
          relatedInfo: relatedInfo,
          permissions: res?.data?.permissions,
        });
        setIsLoading(false);
      })
      .catch(() => {
        userAuthenticationService.setUser({
          ...user,
          relatedInfo: relatedInfo,
          permissions: [],
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!(window.config.oauth?.[0] || window.config.oidc?.[0])) return;
    const authUrl =
      (window?.config?.oauth?.[0] || window?.config?.oidc?.[0])?.authority +
      '/protocol/openid-connect/token';
    if (accessKey && !user?.accessKey) {
      getAccessInfo(accessKey);
    } else if (isEncryptedAuth) {
      verifyEncrypted(location.search).then(res => {
        if (res?.error_code) {
          if (res?.error_code === 'EMSG_14006') {
            setError({ isError: true, type: ERROR_TYPES.EXPIRED });
            return;
          } else if (res?.error_code === 'EMSG_0002') {
            setError({ isError: true, type: ERROR_TYPES.NO_PERMISSION });
            return;
          } else {
            setError({ isError: true, type: ERROR_TYPES.UNKNOWN });
            return;
          }
        }
        const relatedInfo = `app_id=${res.app_id}&patient_id=${res.patient_id}&hash=${res.hash}&expired_at=${res.expired_at}`;
        checkPermissionForHash(relatedInfo);
      });
      return;
    } else if (relatedInfo) {
      if (citizenIdRequired && !relatedParams.get('citizen_id')) {
        // need citizen id first
        console.log('Need citizen id first');
        return;
      }

      setIsLoading(true);
      // If app_id not appear, add full permissions
      if (relatedInfo.includes('app_id')) {
        checkPermissionForHash(relatedInfo);
      } else {
        userAuthenticationService.setUser({
          ...user,
          relatedInfo: relatedInfo,
          permissions: FULL_PERMISSIONS,
        });
        setTimeout(() => setIsLoading(false), 0);
      }
    } else if (accessToken) {
      fetchPermissionToken(accessToken, authUrl).then(res => {
        pushUser(res?.access_token).then(userData => {
          userAuthenticationService.setUser({
            ...(userData || user),
            permission_token: res?.access_token,
          });
          setPermissionTokenReady(true);
          if (isConsultationMode() && !user?.passCode) {
            setIsConsultationPasswordRequired(true);
          }
        });
      });
    } else if (rfToken) {
      refreshToken(rfToken, authUrl).then(res => {
        setAccessToken(res?.access_token, res?.expires_in);
        fetchPermissionToken(res.access_token, authUrl).then(res2 => {
          pushUser(res?.access_token).then(userData => {
            userAuthenticationService.setUser({
              ...(userData || user),
              permission_token: res2?.access_token,
            });
            setPermissionTokenReady(true);
            if (isConsultationMode() && !user?.passCode) {
              setIsConsultationPasswordRequired(true);
            }
          });
        });
      });
    } else {
      // using login form with oauth
      if (window.config.oauth?.[0]) {
        sessionStorage.setItem('ohif-redirect-to', window.location.href);
        navigate('/login');
      }
    }
  }, [accessToken, rfToken, accessKey, relatedInfo]);

  const getAccessInfo = async accessKey => {
    setIsLoading(true);
    if (!window?.config?.risURL) return;
    fetch(window?.config?.risURL + `/studyshares/public/v1/${accessKey}`)
      .then(response => response.json())
      .then(data => {
        const studyInstanceUID = data?.data?.share_study_instance_uid;
        if (data?.data?.is_expired) {
          setError({ isError: true, type: ERROR_TYPES.EXPIRED });
        } else if (!studyInstanceUID) {
          setError({ isError: true, type: ERROR_TYPES.NOT_FOUND });
        } else if (data?.data?.is_require_password) {
          userAuthenticationService.setUser({
            ...user,
            accessKey,
            studyInstanceUID,
            permissions: FULL_PERMISSIONS,
          });
          setIsPasswordRequired(true);
        } else {
          userAuthenticationService.setUser({
            ...user,
            accessKey,
            studyInstanceUID,
            permissions: FULL_PERMISSIONS,
          });
        }
      })
      .catch(() => {
        setError({ isError: true, type: ERROR_TYPES.EXPIRED });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading)
    return <LoadingIndicatorProgress className="h-full w-full bg-black" />;

  if (error.isError) {
    switch (error.type) {
      case ERROR_TYPES.EXPIRED:
        return (
          <ErrorPage
            description={t('You no longer have access due to time limitation.')}
            title={t('Link Expired')}
          />
        );
      case ERROR_TYPES.NOT_FOUND:
        return (
          <ErrorPage>
            <SeriesNotFound />
          </ErrorPage>
        );
      case ERROR_TYPES.NO_PERMISSION:
        return (
          <ErrorPage
            description={t("You don't have permission to access this page.")}
            title={t('Forbidden')}
          />
        );
      default:
        return (
          <ErrorPage
            title={t('Something went wrong')}
            description={t(
              'Please reload the page or try again after 24 hours. If you continue to have issues, contact the Hotline for assistance.'
            )}
          />
        );
    }
  }
  <ErrorPage></ErrorPage>;

  if (citizenIdRequired && !relatedParams.get('citizen_id')) {
    // This is high priority than loading because citizen id need to be enter first, before the
    return (
      <CitizenIdRequiredPage
        // error={error && error === 'true'}
        error={invalidCitizenId}
        onSubmit={async citizenId => {
          setIsLoading(true);
          try {
            const relatedParams = new URLSearchParams(relatedInfo);
            relatedParams.set('citizen_id', citizenId);
            relatedParams.delete('cccd');
            // Add citizen id if need
            const newRelatedInfo = relatedParams.toString();
            const success = await verifyHash(
              newRelatedInfo,
              userAuthenticationService.getAuthorizationHeader(),
              false
            );
            setIsLoading(false);
            if (!success) {
              setInvalidCitizenId(true);
            } else {
              setRelatedInfo(newRelatedInfo);
              userAuthenticationService.setUser({
                ...user,
                relatedInfo: newRelatedInfo,
              });
            }
          } catch (err) {
            setIsLoading(false);
            console.log('ERROR', err);
            setInvalidCitizenId(true);
          }
        }}
      />
    );
  }

  if (isPasswordRequired && !user?.passCode) {
    return (
      <PasswordRequiredPage
        // error={error && error === 'true'}
        error={invalidPasscode?.invalid}
        onSubmit={async pass => {
          setIsLoading(true);
          try {
            const studyDetail = await httpClientService.getStudyDetail(
              queryParams.get('StudyInstanceUIDs'),
              { 'X-SHARE-PWD': pass }
            );
            setIsLoading(false);
            if (!studyDetail?.data) {
              setInvalidPasscode({ invalid: true });
            } else {
              userAuthenticationService.setUser({ ...user, passCode: pass });
            }
          } catch (err) {
            setIsLoading(false);
            console.log('ERROR');
            setInvalidPasscode({ invalid: true });
          }
        }}
      />
    );
  }
  if (isConsultationPasswordRequired && !user?.passCode) {
    if (!permissionTokenReady && !isConsultationGuest)
      return <LoadingIndicatorProgress className="h-full w-full bg-black" />;

    if (isConsultationGuest) {
      return (
        <PasswordEmailRequiredPage
          // error={error && error === 'true'}
          error={invalidPasscode?.invalid}
          errorMessage={invalidPasscode?.message}
          onSubmit={async (email, pass) => {
            setIsLoading(true);
            try {
              const queryParams = new URLSearchParams(location.search);
              const headers = {};
              const appId = queryParams.get('app_id');
              const meetingId = queryParams.get('id');
              const expiredAt = queryParams.get('expired_at');
              const hash = queryParams.get('hash');
              const relatedInfo = `app_id=${appId}&meeting_id=${meetingId}&meeting_password=${pass}&expired_at=${expiredAt}&hash=${hash}`;
              headers['X-PARAMS'] = relatedInfo;
              const success = await httpClientService.verifyConsultationPasswordGuest(
                meetingId,
                {
                  password: pass,
                  email,
                },
                headers
              );
              setRelatedInfo(relatedInfo);
              // userAuthenticationService.setUser({ ...user, relatedInfo });

              setIsLoading(false);
              userAuthenticationService.setUser({ ...user, passCode: pass, relatedInfo, email, username: email });
            } catch (err) {
              setIsLoading(false);
              console.log('ERROR', err.message);
              setInvalidPasscode({
                invalid: true,
                message:
                  (err?.message && MESSAGE.INLINE[err.message]) ||
                  (err?.message && MESSAGE.ERROR[err.message]) ||
                  'Invalid Pass Code',
              });
            }
          }}
        />
      )
    }

    return (
      <PasswordRequiredPage
        // error={error && error === 'true'}
        error={invalidPasscode?.invalid}
        errorMessage={invalidPasscode?.message}
        onSubmit={async pass => {
          setIsLoading(true);
          try {
            const queryParams = new URLSearchParams(location.search);
            const meetingId = queryParams.get('id');
            const headers = {};
            const success = await httpClientService.verifyConsultationPassword(
              meetingId,
              {
                password: pass,
              },
              headers
            );
            setIsLoading(false);
            userAuthenticationService.setUser({ ...user, passCode: pass });
          } catch (err) {
            const data = await err?.response?.json();
            setIsLoading(false);
            console.log('ERROR');
            setInvalidPasscode({
              invalid: true,
              message:
                (data?.error_code && MESSAGE.INLINE[data.error_code]) ||
                (data?.error_code && MESSAGE.ERROR[data.error_code]) ||
                'Invalid Pass Code',
            });
          }
        }}
      />
    );
  }

  if (!relatedInfo && !accessKey && enabled && !user && !isEncryptedAuth) {
    if (accessToken) {
      // If we have token, we are authenticated
      return null;
    }
    return handleUnauthenticated();
  }

  return children;
};

export default PrivateRoute;
